import { Button, Card, CardBody, CardHeader, Divider } from '@heroui/react';
import React, { useCallback } from 'react';

import { useNotification } from '../../../../../contexts/NotificationContext';
import { useGetUserTwilioState } from '../../../hooks/useGetUserTwilioState';
import { useSafelistTwilioUser } from '../../../hooks/useSafelistTwilioUser';

import { InfoTooltip } from './InfoTooltip';
import { TwilioStateContent, TwilioStatus } from './TwilioStateContent';

interface TwilioStateSectionProps {
  userId?: string;
}

const getStatusText = (status: string): string => {
  switch (status) {
    case 'OK':
      return '🟢';
    case 'GEO_BLOCKED':
      return '🔴';
    default:
      return '⚪️';
  }
};

const hasValidTwilioResponse = (
  twilioState: any
): twilioState is { __typename: 'UserTwilioState'; status: string } => {
  return twilioState?.__typename === 'UserTwilioState';
};

export const TwilioStateSection: React.FC<TwilioStateSectionProps> = ({
  userId,
}) => {
  const { addNotification } = useNotification();
  const {
    twilioState,
    loading: queryLoading,
    refetch,
  } = useGetUserTwilioState(userId);
  const { safelistUser, loading: safelistLoading } = useSafelistTwilioUser();

  const handleSafelist = useCallback(async () => {
    if (!userId) {
      addNotification({
        title: 'ERROR',
        message: 'User ID is required to safelist',
        type: 'error',
      });
      return;
    }

    try {
      const result = await safelistUser(userId);

      if (result?.__typename === 'SafelistTwilioUserSuccess') {
        addNotification({
          title: 'SUCCESS',
          message: result.successMessage || 'User safelisted successfully!',
          type: 'success',
        });
        // Refetch the Twilio state to get updated status
        refetch();
      } else if (result?.__typename === 'SafelistTwilioUserFailure') {
        addNotification({
          title: 'ERROR',
          message: result.failureMessage || 'Failed to safelist user',
          type: 'error',
        });
      } else if (
        result?.__typename === 'NotFoundError' ||
        result?.__typename === 'AuthorizationFailure' ||
        result?.__typename === 'InputValidationFailure'
      ) {
        addNotification({
          title: 'ERROR',
          message: result.message || 'Failed to safelist user',
          type: 'error',
        });
      } else {
        addNotification({
          title: 'ERROR',
          message: 'Unknown error occurred while safelisting the user',
          type: 'error',
        });
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';
      addNotification({
        title: 'ERROR',
        message: `Failed to safelist user: ${errorMessage}`,
        type: 'error',
      });
    }
  }, [userId, safelistUser, addNotification, refetch]);

  if (queryLoading) {
    return (
      <Card className="mt-6">
        <CardBody>
          <div className="animate-pulse">
            <div className="h-4 bg-default-200 rounded w-1/4 mb-2"></div>
            <div className="h-3 bg-default-100 rounded w-3/4"></div>
          </div>
        </CardBody>
      </Card>
    );
  }

  if (!twilioState) {
    return null;
  }

  if (!hasValidTwilioResponse(twilioState)) {
    return (
      <Card className="mt-6 border-danger-200">
        <CardBody>
          <p className="text-danger text-sm">
            Error loading Twilio state:{' '}
            {twilioState?.message ||
              twilioState?.ErrorMessage ||
              'Unknown error'}
          </p>
        </CardBody>
      </Card>
    );
  }

  const statusEmoji = getStatusText(twilioState.status);
  const isGeoBlocked = twilioState.status === 'GEO_BLOCKED';

  return (
    <Card className="mt-6">
      <CardHeader className="pb-3">
        <h3 className="text-lg font-semibold text-foreground">Twilio State</h3>
      </CardHeader>
      <Divider />
      <CardBody className="pt-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-sm text-foreground">Status:</span>
            <span className="inline-flex items-center gap-1 text-sm font-medium">
              {statusEmoji} {twilioState.status}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <InfoTooltip
              header="Twilio GeoPermission"
              content={() => (
                <TwilioStateContent
                  status={
                    twilioState.status === 'OK'
                      ? TwilioStatus.OK
                      : TwilioStatus.GEO_BLOCKED
                  }
                />
              )}
            />
            {isGeoBlocked && (
              <Button
                color="primary"
                size="sm"
                onClick={handleSafelist}
                isLoading={safelistLoading}
                isDisabled={safelistLoading || !userId}
              >
                Safelist User
              </Button>
            )}
          </div>
        </div>
      </CardBody>
    </Card>
  );
};
